/**
 * ToastContext — Toast notification system with useToast hook.
 *
 * Position: bottom-right corner
 * Stack: max 3 toasts, newest on top
 * Auto-dismiss: 5s (success/info), manual close required (error)
 *
 * U0.P2 — Global State Patterns
 */

import * as React from 'react';
import { X, CheckCircle2, AlertCircle, Info } from 'lucide-react';
import { cn } from '../../lib/utils';
import { colors, radius } from '../../theme';

// ─── Types ──────────────────────────────────────────────────────────

type ToastVariant = 'success' | 'error' | 'info';

interface Toast {
  id: string;
  variant: ToastVariant;
  message: string;
}

interface ToastContextValue {
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
}

// ─── Context ────────────────────────────────────────────────────────

const ToastContext = React.createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const ctx = React.useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return ctx;
}

// ─── Variant Config ─────────────────────────────────────────────────

const VARIANT_CONFIG: Record<ToastVariant, {
  borderClass: string;
  bgClass: string;
  icon: React.ReactNode;
  autoDismiss: boolean;
}> = {
  success: {
    borderClass: 'border-l-4 border-l-emerald-500',
    bgClass: 'bg-emerald-900/30',
    icon: <CheckCircle2 className="h-4 w-4 text-emerald-400" />,
    autoDismiss: true,
  },
  error: {
    borderClass: 'border-l-4 border-l-rose-500',
    bgClass: 'bg-rose-900/30',
    icon: <AlertCircle className="h-4 w-4 text-rose-400" />,
    autoDismiss: false,
  },
  info: {
    borderClass: 'border-l-4 border-l-slate-500',
    bgClass: 'bg-slate-800',
    icon: <Info className="h-4 w-4 text-slate-400" />,
    autoDismiss: true,
  },
};

const MAX_VISIBLE = 3;
const AUTO_DISMISS_MS = 5000;

// ─── Toast Item ─────────────────────────────────────────────────────

function ToastItem({
  toast,
  onDismiss,
}: {
  toast: Toast;
  onDismiss: (id: string) => void;
}) {
  const config = VARIANT_CONFIG[toast.variant];

  React.useEffect(() => {
    if (!config.autoDismiss) return;
    const timer = setTimeout(() => onDismiss(toast.id), AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [toast.id, config.autoDismiss, onDismiss]);

  return (
    <div
      className={cn(
        'flex items-start gap-2 p-3 border border-slate-700 shadow-lg min-w-[280px] max-w-[380px]',
        radius.card,
        config.borderClass,
        config.bgClass,
        'animate-in slide-in-from-right-5 fade-in duration-200',
      )}
    >
      <span className="shrink-0 mt-0.5">{config.icon}</span>
      <p className={cn('flex-1 text-sm', colors.text.primary)}>{toast.message}</p>
      <button
        onClick={() => onDismiss(toast.id)}
        className={cn('shrink-0 p-0.5 rounded hover:bg-slate-700 transition-colors', colors.text.muted)}
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

// ─── Provider ───────────────────────────────────────────────────────

let toastCounter = 0;

export interface ToastProviderProps {
  children: React.ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = React.useState<Toast[]>([]);

  const addToast = React.useCallback((variant: ToastVariant, message: string) => {
    const id = `toast-${++toastCounter}`;
    setToasts((prev) => [{ id, variant, message }, ...prev]);
  }, []);

  const dismiss = React.useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const contextValue = React.useMemo<ToastContextValue>(() => ({
    success: (message: string) => addToast('success', message),
    error: (message: string) => addToast('error', message),
    info: (message: string) => addToast('info', message),
  }), [addToast]);

  const visibleToasts = toasts.slice(0, MAX_VISIBLE);

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      {/* Toast container — bottom-right, stacked */}
      {visibleToasts.length > 0 && (
        <div className="fixed bottom-4 right-4 z-[9999] flex flex-col gap-2">
          {visibleToasts.map((toast) => (
            <ToastItem key={toast.id} toast={toast} onDismiss={dismiss} />
          ))}
        </div>
      )}
    </ToastContext.Provider>
  );
}
